import ContactCard from '@rsa-digital/evo-shared-components/components/ContactCard/ContactCard';
import { generateKeyForElement } from '@rsa-digital/evo-shared-components/helpers/elementKeyHelper';
import { graphql, useStaticQuery } from 'gatsby';
import React from 'react';
import favicon from 'assets/favicon.png';
import {
  processFavicon,
  processImageAsset,
  processMandatoryImage,
  processOptionalCta,
  unwrapSingleton,
} from 'helpers/csTypeProcessors';
import { trackLinkClick } from 'helpers/eventTracking';
import { CsAsset } from 'types/contentStack';
import { StyledContactCardGrid } from './styles';
import { ContactCardBlockProps } from './types';

type LinkedInLogo = {
  csShareLogos: {
    logos: {
      linkedin: {
        logo: CsAsset;
      };
    };
  };
};

const linkedInLogoQuery = graphql`
  query {
    csShareLogos {
      logos {
        linkedin {
          logo {
            ...Asset
          }
        }
      }
    }
  }
`;

const ContactCardsBlock: React.FC<ContactCardBlockProps> = ({ contact_cards }) => {
  const { csShareLogos } = useStaticQuery<LinkedInLogo>(linkedInLogoQuery);
  const linkedInLogo = processImageAsset(csShareLogos.logos.linkedin.logo);
  const optionalCta = processOptionalCta(contact_cards.cta, 'ContactCardGridCta');

  const hasGlobalCards = contact_cards.global_contact_cards?.length;

  const contactCards = hasGlobalCards
    ? contact_cards.global_contact_cards
    : contact_cards.contact_cards;

  return (
    <StyledContactCardGrid
      id={contact_cards.html_id}
      data-cy={`ContactCardGrid-${contact_cards.heading}`}
      cta={optionalCta}
      heading={contact_cards.heading}
      subheading={contact_cards.subheading}
      alignLeft={contactCards.length <= 1}>
      {contactCards.map((contactCard, index) => (
        <ContactCard
          key={generateKeyForElement(contactCard)}
          data-cy={hasGlobalCards ? `GlobalContactCard${index}` : `ContactCard${index}`}
          profileImage={
            !contactCard.disable_profile_image
              ? contactCard.profile_image
                ? processMandatoryImage(contactCard.profile_image)
                : processFavicon(favicon)
              : undefined
          }
          jobTitle={contactCard.job_title || ''}
          name={contactCard.name || ''}
          emailAddress={contactCard.email_address || ''}
          phoneNumber={
            unwrapSingleton(contactCard.telephone_number)?.telephone_number ?? undefined
          }
          openPhoneInNewTab={
            unwrapSingleton(contactCard.telephone_number)?.open_in_new_tab
          }
          contactDetails={contactCard.contact_details || ''}
          linkedInLink={contactCard.linked_in_link ?? undefined}
          linkedInLogo={linkedInLogo}
          onLinkClick={trackLinkClick}
        />
      ))}
    </StyledContactCardGrid>
  );
};
export default React.memo(ContactCardsBlock);

export const query = graphql`
  fragment ContactCardsBlock on cs__contact_cards {
    html_id
    heading
    subheading
    contact_cards {
      profile_image {
        ...Asset
      }
      disable_profile_image
      job_title
      name
      email_address
      contact_details
      linked_in_link
    }
    global_contact_cards {
      ...ContactCard
    }
    cta {
      ...Cta
    }
  }
`;
